import type { IIntegration } from '@types'
import { getBookingConfig } from '@booking/utils/devEnvironment'
import { EIntegrationKeys } from '@types'

/** GTM */
declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: Gtag
    ym?: (id: number, action: string, ...args: unknown[]) => void
  }
}

type Gtag = (command: string, ...args: unknown[]) => void

function normalizeGoogleTagId(id: string) {
  const normalizedId = id.trim()

  return /^[\w-]+$/.test(normalizedId) ? normalizedId : ''
}

function isGoogleTagManagerId(id: string) {
  return /^GTM-[A-Z0-9]+$/i.test(id)
}

function getGoogleDataLayerSnippet() {
  return `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
  `
}

function initGtag(gaIds: string[]) {
  const primaryId = gaIds[0]
  if (!primaryId) {
    return
  }
  useHead({
    script: [
      { src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryId)}`, async: true },
      {
        innerHTML: `
          ${getGoogleDataLayerSnippet()}
          gtag('js', new Date());
          ${gaIds.map(id => `gtag('config', ${JSON.stringify(id)});`).join('\n          ')}
        `,
      },
    ],
  })
}

function initGoogleTagManager(ids: string[]) {
  useHead({
    script: [
      {
        innerHTML: getGoogleDataLayerSnippet(),
      },
      ...ids.map(id => ({
        innerHTML: `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer',${JSON.stringify(id)});
        `,
      })),
    ],
    noscript: ids.map(id => ({
      innerHTML: `<iframe src="https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(id)}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
      tagPosition: 'bodyOpen',
    })),
  })
}

/** Yandex Metrika */
export function useYandexMetrikaSource() {
  return useState('yandex-metrika-source', () => {
    const params = useRequestURL().searchParams
    return {
      uid: params.get('_ym_uid') || '',
      counter: Number(params.get('_ym_counter')),
    }
  })
}

function getYandexMetrikaSourceSnippet(numericId: number) {
  const { uid, counter } = useYandexMetrikaSource().value
  if (!uid || counter !== numericId) {
    return ''
  }
  // Экранируем `<`, чтобы значение из URL не могло закрыть тег script
  const sourceClientId = JSON.stringify(uid).replace(/</g, '\\u003c')
  return `ym(${numericId},"userParams",{source_client_id:${sourceClientId}});`
}

function initYandexMetrika(id: string, router: ReturnType<typeof useRouter>): boolean {
  const numericId = Number(id)
  if (!Number.isInteger(numericId) || numericId <= 0) {
    return false
  }

  useHead({
    script: [
      {
        innerHTML: `
          (function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
          m[i].l=1*new Date();
          for(var j=0;j<document.scripts.length;j++){if(document.scripts[j].src===r){return;}}
          k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})
          (window,document,"script","https://mc.yandex.ru/metrika/tag.js","ym");
          ym(${numericId},"init",{clickmap:true,trackLinks:true,accurateTrackBounce:true,webvisor:true});
          ${getYandexMetrikaSourceSnippet(numericId)}
        `,
      },
    ],
  })

  if (import.meta.client) {
    router.afterEach(() => {
      window.ym?.(numericId, 'hit', window.location.href)
    })
  }

  return true
}

/** Integrations */
export function useMetrics() {
  const nuxtApp = useNuxtApp()
  const router = useRouter()

  const metrics = {
    [EIntegrationKeys.GTM]: false,
    [EIntegrationKeys.YandexMetrika]: false,
  }

  function init(integrations: IIntegration[]) {
    const backendUrl = getBookingConfig().public.BACKEND_URL
    const gtags = typeof backendUrl === 'string' && backendUrl.includes('business') ? ['G-JZNBPZHDRX'] : []
    const gtmIds: string[] = []

    integrations.forEach(i => {
      switch (i.key) {
        case EIntegrationKeys.GTM: {
          const googleTagId = normalizeGoogleTagId(i.value.code)
          if (!googleTagId) {
            break
          }
          if (isGoogleTagManagerId(googleTagId)) {
            gtmIds.unshift(googleTagId)
          } else {
            gtags.unshift(googleTagId)
          }
          metrics[EIntegrationKeys.GTM] = true
          break
        }
        case EIntegrationKeys.YandexMetrika:
          if (!metrics[EIntegrationKeys.YandexMetrika]) {
            metrics[EIntegrationKeys.YandexMetrika] = initYandexMetrika(i.value.code, router)
          }
          break

        default:
          break
      }
    })

    if (gtags.length) {
      initGtag(gtags)
    }
    if (gtmIds.length) {
      initGoogleTagManager(gtmIds)
    }
  }

  function addGoogleMetricEvent(command: Parameters<Gtag>[0], ...args: unknown[]) {
    if (!metrics[EIntegrationKeys.GTM]) {
      return
    }
    try {
      window.gtag?.(command, ...args)
    } catch (e) {
      console.error('gtag event', e)
    }
  }

  return {
    init: (integrations: IIntegration[]) => nuxtApp.runWithContext(() => init(integrations)),
    addGoogleMetricEvent,
  }
}
